import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type OptionType } from '@/api/common';
import { useSignalTypes } from '@/api/settings';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<OptionType, true> & {
  disabled?: boolean;
  className?: string;
  value?: OptionType[];
};

const SignalTypeMultiSelect = ({
  value,
  size,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const { signalTypes, isLoading } = useSignalTypes();
  const signalTypesById = useMemo(
    () => new Map((signalTypes ?? []).map((signalType) => [signalType.id, signalType])),
    [signalTypes],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((signalType) => signalTypesById.get(signalType.id) ?? signalType)
      .filter((signalType) => Boolean(signalType.name));
  }, [signalTypesById, value]);
  return (
    <Combobox
      data-testid="signal-type-multiselect"
      {...rest}
      multiple
      filteredItems={isLoading ? [] : (signalTypes ?? [])}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {resolvedValue.map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={isLoading ? t`Loading Signal Types...` : t`Select Signal Types...`}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No signal types were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: OptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default SignalTypeMultiSelect;
